import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export interface WidgetCustomization {
  title?: string;
  widgetLabelIcon?: string;
  hidden?: boolean;
  syncMode?: 'linked' | 'isolated' | 'fixed';
  fixedTimestamp?: string;
  selectedCycleWidgetId?: string;
  cycleRailHidden?: boolean;
  slideRow?: 0 | 1 | 2;
  dreamspellDefaultRow?: 'short' | 'medium' | 'tall';
  dreamspellRowDefaults?: Partial<Record<'short' | 'medium' | 'tall', string>>;
  showInShort?: boolean;
  showInMedium?: boolean;
  showInTall?: boolean;
   pagerGridVariant?: 'auto' | 'tall' | 'short';
}

interface WidgetEditorProps {
  open: boolean;
  onClose: () => void;
  widgetId: string;
  currentTitle: string;
  customizations: Record<string, WidgetCustomization>;
  onSave: (widgetId: string, customization: WidgetCustomization) => void;
  onReset: (widgetId: string) => void;
}

export function WidgetEditor({
  open,
  onClose,
  widgetId,
  currentTitle,
  customizations,
  onSave,
  onReset,
}: WidgetEditorProps) {
  const existing = customizations[widgetId] || {};
  const [title, setTitle] = useState(existing.title || currentTitle);
  const [syncMode, setSyncMode] = useState<WidgetCustomization['syncMode']>(
    existing.syncMode ?? 'linked'
  );
  const deriveVisibility = useCallback((source: WidgetCustomization, id: string) => {
    if (id === 'settings') {
      return { showInShort: false, showInTall: true };
    }
    const rawSlideRow = source.slideRow;
    const slideRow = rawSlideRow === 0 || rawSlideRow === 1 ? rawSlideRow : undefined;
    const showInShort = typeof source.showInShort === 'boolean' ? source.showInShort : slideRow === 1;
    // Legacy slideRow=1 implied short-only; default tall on unless explicitly disabled
    const showInTall = typeof source.showInTall === 'boolean' ? source.showInTall : slideRow === 1 ? false : true;
    return { showInShort, showInTall };
  }, []);

  const initialVisibility = deriveVisibility(existing, widgetId);
  const [pagerGridVariant, setPagerGridVariant] = useState<WidgetCustomization['pagerGridVariant']>(existing.pagerGridVariant ?? 'auto');
  const [showInShort, setShowInShort] = useState<boolean>(initialVisibility.showInShort);
  const [showInTall, setShowInTall] = useState<boolean>(initialVisibility.showInTall);

  useEffect(() => {
    if (!open) return;
    const next = customizations[widgetId] || {};
    setTitle(next.title || currentTitle);
    setSyncMode(next.syncMode ?? 'linked');
    const nextVisibility = deriveVisibility(next, widgetId);
    setShowInShort(nextVisibility.showInShort);
    setShowInTall(nextVisibility.showInTall);
    setPagerGridVariant(next.pagerGridVariant ?? 'auto');
  }, [customizations, currentTitle, deriveVisibility, open, widgetId]);

  const handleSave = () => {
    // Always include placement flags; keep legacy slideRow for backward compatibility
    const legacySlideRow: 0 | 1 = showInShort && !showInTall ? 1 : 0;
    const effectiveVariant: WidgetCustomization['pagerGridVariant'] = showInShort && showInTall
      ? pagerGridVariant ?? 'auto'
      : showInShort
        ? 'short'
        : 'tall';

    const customization: WidgetCustomization = {
      slideRow: legacySlideRow,
      showInShort,
      showInTall,
      pagerGridVariant: effectiveVariant,
    };
    // Only add title if it changed
    if (title !== currentTitle) {
      customization.title = title;
    }
    // Only add syncMode if it's not the default 'linked'
    if (syncMode !== 'linked') {
      customization.syncMode = syncMode;
    }
    onSave(widgetId, customization);
    onClose();
  };

  const handleReset = () => {
    onReset(widgetId);
    setTitle(currentTitle);
    setSyncMode('linked');
    setShowInShort(false);
    setShowInTall(true);
    setPagerGridVariant('auto');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-transparent" aria-describedby="widget-editor-description">
        <DialogHeader>
          <DialogTitle className="font-display">Edit Widget</DialogTitle>
        </DialogHeader>

        {/* Hidden description for accessibility */}
        <p id="widget-editor-description" className="sr-only">
          Configure this dashboard widget: title, link behavior, and slider position.
        </p>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="widget-title">Widget Title</Label>
            <Input
              id="widget-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter custom title"
              className="bg-background"
            />
            <p className="text-xs text-muted-foreground">
              Original: {currentTitle}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="widget-sync">Link behavior</Label>
            <div className="flex items-center justify-between gap-4 px-3 py-2">
              <div>
                <p className="text-sm font-medium">Link interactions</p>
                <p className="text-xs text-muted-foreground">
                  Allow this widget to update the shared timeline.
                </p>
              </div>
              <Switch
                id="widget-sync"
                checked={syncMode === 'linked'}
                onCheckedChange={(value) => setSyncMode(value ? 'linked' : 'isolated')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Skin view placement</Label>
            <div className="flex flex-col gap-3 px-3 py-2 border rounded-md">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Tall slider (bottom)</p>
                  <p className="text-xs text-muted-foreground">Show this widget in the tall slider row.</p>
                </div>
                <Switch
                  id="widget-tall"
                  checked={showInTall}
                  onCheckedChange={(value) => {
                    setShowInTall(value);
                    if (!value && !showInShort) setShowInShort(true);
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Short slider (top)</p>
                  <p className="text-xs text-muted-foreground">Also show a compact clone in the short slider.</p>
                </div>
                <Switch
                  id="widget-short"
                  checked={showInShort}
                  onCheckedChange={(value) => {
                    setShowInShort(value);
                    if (!value && !showInTall) setShowInTall(true);
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                At least one slider stays enabled. Turn both on to show clones in both rows.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Pager & grid preference</Label>
            <div className="flex flex-col gap-2 px-3 py-2 border rounded-md">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Choose which variant shows in pager/grid</p>
                  <p className="text-xs text-muted-foreground">Short requires a short view; falls back to tall if missing.</p>
                </div>
                <select
                  className="text-sm bg-background border rounded px-2 py-1"
                  value={pagerGridVariant ?? 'auto'}
                  onChange={(e) => setPagerGridVariant(e.target.value as WidgetCustomization['pagerGridVariant'])}
                >
                  <option value="auto">Auto (prefer tall)</option>
                  <option value="tall">Tall</option>
                  <option value="short">Short</option>
                </select>
              </div>
              <p className="text-xs text-muted-foreground">
                When both sliders are enabled, this decides which variant pager/grid uses.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button type="button" variant="ghost" onClick={handleReset}>
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
